import React, { memo, useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Plus, Trash2, Sparkles, ListTodo, ChevronRight } from 'lucide-react';
import { InlineEditable } from '../tasks/InlineEditable';
import { FieldPicker, TaskAttributeRow } from '../tasks/TaskFieldChips';
import { DueBadge } from '../tasks/DueBadge';
import { toLocalDay } from '../tasks/taskDates';
import { STATUS_META, STATUS_ORDER, isDemoted } from '../tasks/taskMeta';
import { cn } from '../../lib/utils';

/**
 * The whole board, condensed into a 440px column.
 *
 * The point of this tab is that the user is never limited to reviewing the
 * agent's work — they can add, retitle, re-scope and delete anything from right
 * here without leaving the conversation. Tasks the agent touched in this
 * session are marked so it stays obvious who did what.
 */

const SPRING = { type: 'spring', stiffness: 260, damping: 24 };

// ═══════════════════════════════════════════════════════════════════════════════
// ROW
// ═══════════════════════════════════════════════════════════════════════════════

const MiniTaskRow = memo(function MiniTaskRow({ task, touchedByAgent, readOnly, reduceMotion, onUpdate, onDelete }) {
    const [expanded, setExpanded] = useState(false);
    const isDone = task.status === 'done';

    const patch = useCallback((updates) => onUpdate(task.id, updates), [onUpdate, task.id]);
    const hasNotes = !!(task.description && task.description.trim());

    return (
        <motion.div
            layout={!reduceMotion}
            initial={reduceMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 10, height: 0 }}
            transition={SPRING}
            data-testid={`mini-task-${task.id}`}
            className={cn(
                'group/mini relative flex gap-2.5 border-b border-white/5 py-2.5 pl-3 pr-2 transition-colors hover:bg-white/[0.03]',
                isDone && 'opacity-55',
            )}
        >
            <span
                aria-hidden
                className={cn('absolute left-0 top-0 h-full w-[2px]', touchedByAgent ? 'bg-primary/70' : 'bg-white/[0.06]')}
            />

            <button
                type="button"
                title={`Mark as ${isDone ? 'to do' : 'done'}`}
                data-testid={`mini-task-toggle-${task.id}`}
                disabled={readOnly}
                onClick={() => patch({ status: isDone ? 'todo' : 'done' })}
                className="mt-1 flex-shrink-0 self-start disabled:cursor-default"
            >
                {isDone ? (
                    <STATUS_META.done.icon className="h-4 w-4 text-emerald-500" />
                ) : (
                    <span className="block h-4 w-4 rounded-full border-2 border-muted-foreground transition-colors hover:border-emerald-500 hover:bg-emerald-500/20" />
                )}
            </button>

            <div className="min-w-0 flex-1">
                <div className="flex items-start gap-1">
                    <div className="min-w-0 flex-1">
                        <InlineEditable
                            value={task.title}
                            disabled={readOnly}
                            onCommit={(title) => title && patch({ title })}
                            placeholder="Untitled task"
                            testId={`mini-task-title-${task.id}`}
                            ariaLabel="Task title"
                            className={cn('-ml-2 text-sm font-medium', isDone && 'text-muted-foreground line-through')}
                        />
                    </div>
                    {touchedByAgent ? (
                        <Sparkles
                            aria-label="Touched by the agent this session"
                            className="mt-1.5 h-3 w-3 flex-shrink-0 text-primary/70"
                        />
                    ) : null}
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <FieldPicker
                        field="status"
                        value={task.status}
                        disabled={readOnly}
                        onChange={(status) => patch({ status })}
                        testId={`mini-task-${task.id}-status`}
                    />
                    <TaskAttributeRow task={task} onUpdate={readOnly ? undefined : patch} disabled={readOnly} />
                    <DueBadge
                        due={task.due_date}
                        size="sm"
                        testId={`mini-task-due-${task.id}`}
                        onClear={readOnly ? undefined : () => patch({ due_date: null })}
                    />

                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium text-muted-foreground/70 transition-colors hover:text-foreground"
                    >
                        <ChevronRight className={cn('h-3 w-3 transition-transform duration-200', expanded && 'rotate-90')} />
                        {hasNotes ? 'Notes' : 'Add notes'}
                    </button>
                </div>

                <AnimatePresence initial={false}>
                    {expanded || hasNotes ? (
                        <motion.div
                            key="notes"
                            initial={reduceMotion ? false : { opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.18 }}
                            className="overflow-hidden"
                        >
                            <InlineEditable
                                value={task.description}
                                disabled={readOnly}
                                multiline
                                onCommit={(description) => patch({ description })}
                                placeholder="Add notes… (@ to reference a file)"
                                testId={`mini-task-notes-${task.id}`}
                                ariaLabel="Task notes"
                                className="-ml-2 mt-1 text-xs text-muted-foreground/80"
                            />
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </div>

            {!readOnly ? (
                <button
                    type="button"
                    title="Delete task"
                    aria-label="Delete task"
                    data-testid={`mini-task-delete-${task.id}`}
                    onClick={() => onDelete(task.id)}
                    className="mt-0.5 flex h-8 w-8 flex-shrink-0 self-start items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-red-500/20 hover:text-red-400 md:opacity-0 md:group-hover/mini:opacity-100 md:focus-visible:opacity-100"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            ) : null}
        </motion.div>
    );
});

// ═══════════════════════════════════════════════════════════════════════════════
// QUICK ADD
// ═══════════════════════════════════════════════════════════════════════════════

const QuickAdd = memo(function QuickAdd({ onCreate }) {
    const [value, setValue] = useState('');
    const [focused, setFocused] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        const title = value.trim();
        if (!title) return;
        onCreate(title);
        setValue('');
    };

    return (
        <form onSubmit={submit} className="border-b border-white/5 px-3 py-2.5">
            <div
                className={cn(
                    'flex items-center gap-2 rounded-xl border px-2.5 py-2 transition-colors',
                    focused ? 'border-primary/50 bg-primary/[0.06]' : 'border-dashed border-white/10 hover:border-white/20',
                )}
            >
                <Plus className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <input
                    value={value}
                    data-testid="mini-task-create-input"
                    onChange={(e) => setValue(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    placeholder="Add a task…"
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
                />
                <AnimatePresence>
                    {value.trim() ? (
                        <motion.button
                            key="submit"
                            type="submit"
                            data-testid="mini-task-create-submit"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="flex-shrink-0 rounded-lg bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground transition-colors hover:brightness-110"
                        >
                            Add
                        </motion.button>
                    ) : null}
                </AnimatePresence>
            </div>
        </form>
    );
});

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════════

function TaskBoardMiniImpl({ tasks = [], agentTouchedIds, readOnly = false, onCreate, onUpdate, onDelete }) {
    const reduceMotion = useReducedMotion();

    // Group once per render; the board is stable between keystrokes.
    //
    // Sorted by due date inside each column, which puts overdue work at the top
    // and undated work at the bottom. This panel is where a scheduling
    // conversation happens — asking what's late and then having to hunt for it
    // in creation order is the wrong answer to the question that opened it.
    const groups = useMemo(() => {
        const buckets = { 'todo': [], 'in-progress': [], 'done': [] };
        for (const task of tasks) {
            // Same pool the board shows. A parked task appearing here would be
            // the one place the backlog leaks back into your field of view.
            if (isDemoted(task)) continue;
            (buckets[task.status] ?? buckets['todo']).push(task);
        }
        // Infinity, not 0: a task with no date sorts last rather than pretending
        // to be due at the epoch and burying everything that is actually late.
        const dueAt = (task) => toLocalDay(task.due_date)?.getTime() ?? Infinity;
        for (const bucket of Object.values(buckets)) {
            bucket.sort((a, b) => dueAt(a) - dueAt(b));
        }
        return buckets;
    }, [tasks]);

    // Counted from the buckets, not from `tasks` — on a project whose every task
    // is parked, the raw length is non-zero and this would render three empty
    // columns instead of saying there is nothing to show.
    const shown = STATUS_ORDER.reduce((n, status) => n + groups[status].length, 0);

    if (shown === 0) {
        return (
            <div className="flex h-full flex-col">
                {!readOnly ? <QuickAdd onCreate={onCreate} /> : null}
                <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]">
                        <ListTodo className="h-5 w-5 text-muted-foreground/60" />
                    </div>
                    <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                        No tasks yet
                    </p>
                    <p className="mt-3 max-w-[26ch] text-xs leading-relaxed text-muted-foreground/60">
                        Add one above, or ask the agent to break your project down into a plan.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div>
            {!readOnly ? <QuickAdd onCreate={onCreate} /> : null}

            {STATUS_ORDER.map((status) => {
                const bucket = groups[status];
                if (!bucket.length) return null;
                const meta = STATUS_META[status];

                return (
                    <section key={status}>
                        <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-white/10 bg-[#0b0b0e]/95 px-3 py-1.5 backdrop-blur-xl">
                            <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
                            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                {meta.label}
                            </span>
                            <span className="font-mono text-[10px] tabular-nums text-muted-foreground/50">
                                {bucket.length}
                            </span>
                        </header>

                        <div className="cv-auto">
                            <AnimatePresence initial={false}>
                                {bucket.map((task) => (
                                    <MiniTaskRow
                                        key={task.id}
                                        task={task}
                                        touchedByAgent={agentTouchedIds.has(task.id)}
                                        readOnly={readOnly}
                                        reduceMotion={reduceMotion}
                                        onUpdate={onUpdate}
                                        onDelete={onDelete}
                                    />
                                ))}
                            </AnimatePresence>
                        </div>
                    </section>
                );
            })}
        </div>
    );
}

export const TaskBoardMini = memo(TaskBoardMiniImpl);
export default TaskBoardMini;
